import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../App';
import { helpAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, Download, Loader2, ShieldCheck, User, Users, AlertTriangle, Info } from 'lucide-react';

const ROLE_ICON = {
  super_admin: <ShieldCheck size={18} />,
  manager: <Users size={18} />,
  employee: <User size={18} />,
};

const HelpCenter = () => {
  const { user } = useAuth();
  const [manuals, setManuals] = useState([]);
  const defaultRole =
    user?.role === 'super_admin' ? 'super_admin' :
    user?.role === 'manager' ? 'manager' :
    ['hr_admin', 'payroll_admin'].includes(user?.role) ? 'super_admin' :
    'employee';
  const [selected, setSelected] = useState(defaultRole);
  const [markdown, setMarkdown] = useState('');
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await helpAPI.listManuals();
        setManuals(r.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await helpAPI.getManual(selected);
        setMarkdown(r.data?.markdown || '');
        setMeta({ source: r.data?.source, screenshotsAvailable: r.data?.screenshots_available });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load manual');
        setMarkdown('');
        setMeta({});
      } finally { setLoading(false); }
    })();
  }, [selected]);

  const downloadPdf = async (role) => {
    const token = localStorage.getItem('token');
    const r = await fetch(helpAPI.pdfUrl(role), { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error('PDF download failed'); return; }
    const blob = await r.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${role}_manual.pdf`;
    link.click();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen size={22} />Help Center</h1>
        <p className="text-muted-foreground text-sm">Role-specific user manuals with screenshots. Also available as PDFs.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="help-tabs">
        {manuals.map((m) => (
          <Button
            key={m.role}
            variant={selected === m.role ? 'default' : 'outline'}
            onClick={() => setSelected(m.role)}
            disabled={!m.available}
            data-testid={`help-tab-${m.role}`}
          >
            <span className="mr-2">{ROLE_ICON[m.role]}</span>
            {m.title}
            {m.has_pdf && <Badge variant="secondary" className="ml-2 text-xs">{m.pdf_size_kb} KB</Badge>}
            {!m.available && <Badge variant="destructive" className="ml-2 text-xs">missing</Badge>}
          </Button>
        ))}
        <div className="flex-1" />
        <Button variant="outline" onClick={() => downloadPdf(selected)} data-testid="help-download-pdf">
          <Download size={14} className="mr-1" />Download PDF
        </Button>
      </div>

      {meta.source === 'bundled' && (
        <div className="flex items-start gap-2 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-3" data-testid="help-bundled-banner">
          <Info size={14} className="mt-0.5" />
          <div>
            <div className="font-medium">Serving bundled copy</div>
            The <code>/app/docs</code> folder is not deployed on this server. The manual text is included in the backend package, but screenshots and PDF may not be available. To restore them on your VPS:
            <pre className="mt-1 bg-white/60 border rounded px-2 py-1 overflow-x-auto">git pull  # if docs are committed
python3 /app/docs/capture_screenshots.py  # regenerate screenshots (needs Playwright)
python3 /app/docs/build_pdfs.py           # rebuild PDFs</pre>
          </div>
        </div>
      )}
      {meta.source && !meta.screenshotsAvailable && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 border rounded p-3" data-testid="help-no-screenshots-banner">
          <AlertTriangle size={14} className="mt-0.5" />
          Screenshots are not available on this deployment — the text below still works. Run <code className="mx-1">python3 /app/docs/capture_screenshots.py</code> from the app root to generate them.
        </div>
      )}

      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading manual…</div>
          ) : !markdown ? (
            <div className="text-sm text-muted-foreground text-center p-8">Manual not available.</div>
          ) : (
            <article className="help-markdown prose prose-sm max-w-none dark:prose-invert" data-testid="help-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ node, ...props }) => (
                    <img
                      {...props}
                      className="my-4 border rounded shadow-sm max-w-full"
                      loading="lazy"
                      onError={(e) => { e.target.style.opacity = '0.3'; }}
                    />
                  ),
                  h1: ({ node, ...props }) => <h1 {...props} className="text-3xl font-bold mt-2 mb-4" />,
                  h2: ({ node, ...props }) => <h2 {...props} className="text-xl font-semibold mt-8 mb-3 pb-1 border-b" />,
                  h3: ({ node, ...props }) => <h3 {...props} className="text-base font-semibold mt-5 mb-2" />,
                  p: ({ node, ...props }) => <p {...props} className="my-2 leading-relaxed" />,
                  li: ({ node, ...props }) => <li {...props} className="my-1" />,
                  table: ({ node, ...props }) => <table {...props} className="w-full border-collapse my-4 text-sm" />,
                  th: ({ node, ...props }) => <th {...props} className="border p-2 bg-muted/40 text-left font-semibold" />,
                  td: ({ node, ...props }) => <td {...props} className="border p-2 align-top" />,
                  code: ({ node, inline, ...props }) =>
                    inline
                      ? <code {...props} className="px-1 py-0.5 rounded bg-muted text-xs font-mono" />
                      : <code {...props} className="block p-3 rounded bg-muted text-xs font-mono overflow-x-auto" />,
                  blockquote: ({ node, ...props }) => <blockquote {...props} className="border-l-4 border-primary/50 pl-3 my-3 italic text-muted-foreground" />,
                }}
              >{markdown}</ReactMarkdown>
            </article>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenter;
